import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconPlus } from '@tabler/icons-react';
import { Button, Card, LoadingOverlay, Paper } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import PageHeader from '../../../../components/core/PageHeader/PageHeader';
import DataView from './components/DataView';
import { VehicleType } from '../../../../models/core/vehicles.type';
import { Filter, FilterData } from './components/Filter';
import PageContent from '../../../../components/core/PageContent/PageContent';
import vehiclesVehicle from '../../../../services/core/vehicles.service';
import { Feature, SessionStorageKey } from '../../../../utils/constants.utils';
import { Permission } from '../../../../models/core/departments.type';

function VehicleSearch() {
  const navigate = useNavigate();

  const [data, setData] = useState<VehicleType[]>([]);
  const [loading, setLoading] = useState(false);

  /* eslint-disable @typescript-eslint/no-use-before-define */
  useEffect(() => {
    const cacheResult = JSON.parse(
      sessionStorage.getItem(SessionStorageKey.VehicleSearch) || '[]'
    ) as VehicleType[];

    if (cacheResult.length > 0) {
      setData(cacheResult);
    } else {
      filter({ residuoVeiculo: '' });
    }
  }, []);

  const clear = () => {
    sessionStorage.removeItem(SessionStorageKey.VehicleSearch);
    setData([]);
  };

  const filter = async (inputFilterData: FilterData) => {
    try {
      setLoading(true);
      const result = await vehiclesVehicle.search({
        residuoVeiculo: inputFilterData?.residuoVeiculo || null,
        limit: 1000,
        offset: 0,
      });
      sessionStorage.setItem(SessionStorageKey.VehicleSearch, JSON.stringify(result));
      setData(result);
    } catch (error: any) {
      showNotification({
        title: 'Veículos',
        message: error?.isBusinessException ? error.description : 'Não foi possível pesquisar veículos.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmActionResult = async (item: VehicleType, action: string, confirmed: boolean) => {
    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);

      switch (action) {
        case 'excluir':
          await vehiclesVehicle.delete({ idResiduoVeiculo: item.idResiduoVeiculo });
          setData(data.filter((x) => x.idResiduoVeiculo !== item.idResiduoVeiculo));
          break;
        case 'visualizar':
          navigate(`/vehicles/${item.idResiduoVeiculo}`, {
            state: {
              vehicle: item,
            },
          });
          return;
        case 'editar':
          navigate(`/vehicles/${item.idResiduoVeiculo}/edit`, {
            state: {
              vehicle: item,
            },
          });
          return;
        default:
          break;
      }

      showNotification({
        title: 'Veículos',
        message: `Veículo excluído com sucesso.`,
        color: 'green',
      });
    } catch (error: any) {
      showNotification({
        title: 'Veículos',
        message: error?.isBusinessException ? error.description : `Não foi possível ${action} o veículo.`,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <PageHeader
        feature={Feature.Reference.Vehicle}
        title="Veículos"
        description="Gerencie veículos."
        buttons={[
          <Button
            key="Adicionar"
            color="primary"
            leftSection={<IconPlus size={18} />}
            disabled={loading}
            onClick={() => navigate('/vehicles/add')}
            data-permission={Permission.VehicleAdd}
          >
            Adicionar
          </Button>,
        ]}
      />
      <PageContent>
        <Filter filter={filter} clear={clear} loading={loading} />
        <div style={{ position: 'relative' }}>
          <LoadingOverlay visible={loading} />
          <Paper shadow="xs" p="md" withBorder>
            <DataView data={data} confirmActionResult={confirmActionResult} />
          </Paper>
        </div>
      </PageContent>
    </Card>
  );
}

export default VehicleSearch;
